import React, { useState, useEffect } from 'react';
import { Search, Loader2, X, Brain } from 'lucide-react';
import { Player } from '@lottiefiles/react-lottie-player';
import evidenceSearchAnimation from '../../lottie/evidenceSearch.json';

interface EvidenceSearchButtonProps {
  show: boolean;
  position: { x: number; y: number };
  selectedText: string;
  onSearch: (selectedText: string) => Promise<void>;
  onClose: () => void;
  isLoading?: boolean; // External loading state
}

export const EvidenceSearchButton: React.FC<EvidenceSearchButtonProps> = ({
  show,
  position,
  selectedText,
  onSearch,
  onClose,
  isLoading: externalLoading = false
}) => {
  const [internalLoading, setInternalLoading] = useState(false);
  const [searchProgress, setSearchProgress] = useState(0);
  const [smartPosition, setSmartPosition] = useState({ x: 0, y: 0, placement: 'below' });
  
  // Use external loading if provided, otherwise use internal
  const isLoading = externalLoading || internalLoading;

  // Calculate smart positioning based on available screen space
  useEffect(() => {
    if (!show) return;

    const calculatePosition = () => {
      const buttonHeight = isLoading ? 320 : 180; // Estimated height with/without loading animation
      const buttonWidth = 288; // max-w-72 = 18rem = 288px
      const padding = 20; // Safe padding from screen edges
      
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const scrollTop = window.scrollY;
      
      let finalX = position.x;
      let finalY = position.y;
      let placement = 'below';
      
      const offsetDistance = 90; // Small distance from cursor position
      
      // Check if there's enough space below the cursor position
      const spaceBelow = viewportHeight - (position.y - scrollTop) - padding;
      const spaceAbove = (position.y - scrollTop) - padding;
      
      if (spaceBelow >= buttonHeight) {
        // Position below the cursor with small offset
        finalY = position.y + offsetDistance;
        placement = 'below';
      } else if (spaceAbove >= buttonHeight) {
        // Position above the cursor with offset
        finalY = position.y - buttonHeight - offsetDistance;
        placement = 'above';
      } else {
        // Not enough space in either direction, prefer below but adjust
        if (spaceBelow > spaceAbove) {
          finalY = position.y + offsetDistance;
          placement = 'below';
        } else {
          finalY = Math.max(scrollTop + padding, position.y - buttonHeight - offsetDistance);
          placement = 'above';
        }
      }
      
      // Ensure button doesn't go off-screen horizontally
      const halfButtonWidth = buttonWidth / 2;
      if (finalX - halfButtonWidth < padding) {
        finalX = halfButtonWidth + padding;
      } else if (finalX + halfButtonWidth > viewportWidth - padding) {
        finalX = viewportWidth - halfButtonWidth - padding;
      }
      
      setSmartPosition({ x: finalX, y: finalY, placement });
    };

    calculatePosition();
    
    // Recalculate on window resize or scroll
    const handleResize = () => calculatePosition();
    const handleScroll = () => calculatePosition();
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [show, position.x, position.y, isLoading]);

  useEffect(() => {
    if (!isLoading) {
      setSearchProgress(0);
      return;
    }

    let progress = 0;
    const progressInterval = setInterval(() => {
      progress += Math.random() * 3 + 1;
      // Cap at 90% during loading, only reach 100% on success
      if (progress >= 90) {
        progress = 90;
        clearInterval(progressInterval);
      }
      setSearchProgress(progress);
    }, 200);

    return () => clearInterval(progressInterval);
  }, [isLoading]);

  const handleSearch = async () => {
    if (!selectedText.trim() || isLoading) return;
    
    setInternalLoading(true);
    try {
      await onSearch(selectedText);
      // Slowly animate to 100% on successful API call
      const completeAnimation = () => {
        let currentProgress = searchProgress;
        const finishInterval = setInterval(() => {
          currentProgress += 2;
          if (currentProgress >= 100) {
            currentProgress = 100;
            clearInterval(finishInterval);
          }
          setSearchProgress(currentProgress);
        }, 50);
      };
      completeAnimation();
    } catch (error) {
      console.error('Evidence search failed:', error);
      // Don't animate to 100% on error - leave it at current percentage
    } finally {
      setInternalLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="absolute z-[9999] pointer-events-auto transition-all duration-200 ease-out"
      style={{
        left: `${smartPosition.x}px`,
        top: `${smartPosition.y}px`,
        transform: 'translateX(-50%)',
      }}
    >
      {/* Compact Card */}
      <div className="relative bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden max-w-72">
        <div className="relative z-10">
          {/* Compact Header */}
          <div className="px-3 pt-3 pb-2">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-green-600 rounded-md">
                <Brain className="w-3 h-3 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-xs font-semibold text-gray-900">Medical Evidence AI</h3>
              </div>
              <button
                onClick={onClose}
                disabled={isLoading}
                className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                aria-label="Close"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* Selected Text - Compact */}
          <div className="px-3 pb-2">
            <div className="bg-gray-50 rounded-md p-2 border border-gray-100">
              <div className="text-xs text-gray-800 leading-relaxed">
                "{selectedText.length > 80 ? selectedText.substring(0, 80) + '...' : selectedText}"
              </div>
            </div>
          </div>

          {/* Compact Action Button */}
          <div className="px-3 pb-3">
            <button
              onClick={handleSearch}
              disabled={isLoading || !selectedText.trim()}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span>Searching...</span>
                </>
              ) : (
                <>
                  <Search className="w-3 h-3" />
                  <span>Analyze Evidence</span>
                </>
              )}
            </button>
          </div>

          {/* Compact Search Progress with Lottie Animation */}
          {isLoading && (
            <div className="px-3 pb-3">
              <div className="bg-green-50 rounded-md p-3 border border-green-200">
                {/* Lottie Animation */}
                <div className="flex items-center justify-center mb-3">
                  <div className="w-12 h-12">
                    <Player
                      autoplay
                      loop
                      src={evidenceSearchAnimation}
                      style={{ height: '100%', width: '100%' }}
                    />
                  </div>
                </div>

                {/* Progress */}
                <div className="mb-2">
                  <div className="flex justify-between text-xs text-green-700 mb-1">
                    <span>Searching evidence</span>
                    <span>{Math.round(searchProgress)}%</span>
                  </div>
                  <div className="w-full bg-green-200 rounded-full h-1.5">
                    <div 
                      className="h-full bg-green-500 rounded-full transition-all duration-200 ease-out"
                      style={{ width: `${searchProgress}%` }}
                    />
                  </div>
                </div>

                {/* Search Status */}
                <div className="text-center">
                  <span className="text-xs text-green-600 font-medium">
                    Analyzing medical evidence...
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Arrow Pointer - dynamically positioned based on placement */}
        {smartPosition.placement === 'below' ? (
          /* Arrow pointing up (when button is below selection) */
          <div className="absolute -top-[6px] left-1/2 transform -translate-x-1/2">
            <div className="w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-l-transparent border-r-transparent border-b-white" />
            <div className="absolute -top-[1px] left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[7px] border-r-[7px] border-b-[7px] border-l-transparent border-r-transparent border-b-gray-200" />
          </div>
        ) : (
          /* Arrow pointing down (when button is above selection) */
          <div className="absolute -bottom-[6px] left-1/2 transform -translate-x-1/2">
            <div className="w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-white" />
            <div className="absolute -bottom-[1px] left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[7px] border-r-[7px] border-t-[7px] border-l-transparent border-r-transparent border-t-gray-200" />
          </div>
        )}
      </div>
    </div>
  );
}; 